// src/features/scheduling/services/schedulingApi.ts
import { api } from "@/services/api";
import type {
  Visitor,
  PersonSearchParams,
  CreatePersonRequest,
  VendorLocation,
  ScheduleAppointmentRequest,
  ScheduleAppointmentResponse,
} from "../types";

export const schedulingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Search visitors by various criteria
    searchPersons: builder.query<Visitor[], PersonSearchParams & { clientId?: number }>({
      query: (params) => ({
        url: "/visitors/search",
        method: "GET",
        params: {
          ...(params.name && { name: params.name }),
          ...(params.email && { email: params.email }),
          ...(params.phone && { phone: params.phone }),
          ...(params.dateOfBirth && { dateOfBirth: params.dateOfBirth }),
          ...(params.clientId && { clientId: params.clientId }),
        },
      }),
      providesTags: ["Users"],
    }),

    // Get all visitors (for initial list)
    listPersons: builder.query<Visitor[], { clientId?: number } | void>({
      query: (params) => ({
        url: "/visitors",
        params: {
          clientId: params?.clientId
        }
      }),
      providesTags: ["Users"],
    }),

    // Create new visitor
    createPerson: builder.mutation<Visitor, CreatePersonRequest>({
      query: (body) => ({
        url: "/visitors",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Users"],
    }),

    // Get visitor by ID
    getPersonById: builder.query<Visitor, number>({
      query: (id) => `/visitors/${id}`,
      providesTags: ["Users"],
    }),

    // Search vendor locations by zip code and radius
    searchVendorLocations: builder.query<
      VendorLocation[],
      { zipCode: string; radius: number; testIds?: number[]; drugTestIds?: number[]; clientId?: number }
    >({
      query: (body) => ({
        url: "/vendors/locations/search",
        method: "POST",
        body,
      }),
      providesTags: ["Vendors", "Clinics"],
    }),

    // Get geocode for zip code (for map centering)
    getGeocode: builder.query<{ lat: number; lng: number }, string>({
      query: (zipCode) => ({
        url: `/geocode/${zipCode}`,
        method: "GET",
      }),
    }),

    // Schedule appointment (visitor + clinic + tests)
    scheduleAppointment: builder.mutation<ScheduleAppointmentResponse, ScheduleAppointmentRequest>({
      query: (body) => ({
        url: "/scheduling/appointments",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Vendors", "Clinics"],
    }),

    // Check existing appointment
    checkExistingAppointment: builder.query<{ hasAppointment: boolean }, { visitorId: number; date: string }>({
      query: ({ visitorId, date }) => ({
        url: "/scheduling/appointments/check",
        method: "GET",
        params: { visitorId, date },
      }),
    }),
    // Get margin-adjusted prices for a specific clinic
    getClinicPrices: builder.query<Record<number, number>, { clinicId: number; testIds: number[]; clientId?: number }>({
      query: ({ clinicId, testIds, clientId }) => ({
        url: `/vendors/locations/${clinicId}/prices`,
        method: "POST",
        body: {
          testIds,
          clientId,
        },
      }),
      providesTags: ["Vendors", "Clinics"],
    }),
  }),
});

export const {
  useSearchPersonsQuery,
  useLazySearchPersonsQuery,
  useListPersonsQuery,
  useCreatePersonMutation,
  useGetPersonByIdQuery,
  useSearchVendorLocationsQuery,
  useLazySearchVendorLocationsQuery,
  useGetGeocodeQuery,
  useLazyGetGeocodeQuery,
  useScheduleAppointmentMutation,
  useLazyCheckExistingAppointmentQuery,
  useLazyGetClinicPricesQuery,
} = schedulingApi;
